import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import ProcessDiagram from "../components/ProcessDiagram";
import HubspotPlaceholder from "../components/HubspotPlaceholder";
import { SmileFigure } from "../components/Illustrations";
import { MapPin, Mail, ArrowUpRight } from "lucide-react";
import jobs from "../data/jobs.json";

const CANDIDATE_STEPS = [
  { overline: "Step 01", title: "Tell us about you", body: "Send your CV or a few notes about what you're looking for. We'll book a proper conversation — no rushed phone screen." },
  { overline: "Step 02", title: "We map your options", body: "Together we shape what 'right' looks like — role, team, money, lifestyle — and which of our live or upcoming briefs fit." },
  { overline: "Step 03", title: "Introductions, not blasts", body: "We only put you forward where we genuinely see a fit. Each introduction comes with prep, context, and honest expectations." },
  { overline: "Step 04", title: "Through to offer", body: "We support you through interviews, references, negotiation and onboarding — and stay in touch well past day one." },
];

const APPLY_EMAIL = "Bronwyn@uniterecruitment.co.nz";

function buildMailto(job) {
  const subject = encodeURIComponent(`Application — ${job.title} (${job.location})`);
  const body = encodeURIComponent(
    `Hi Bronwyn,\n\nI'd like to apply for the ${job.title} role based in ${job.location}.\n\nMy CV is attached.\n\n[A quick note about me / why I'm a fit — feel free to edit or delete]\n\nThanks,\n`
  );
  return `mailto:${APPLY_EMAIL}?subject=${subject}&body=${body}`;
}

export default function FindAJob() {
  return (
    <div data-testid="page-find-a-job">
      <Seo
        title="Find a Job"
        description="Find work that actually fits your life. Live NZ roles across tech, sales and support — from a boutique recruiter that listens first."
        image="/og-brand.png"
        path="/find-a-job"
      />
      <PageHero
        overline="For candidates"
        title="Find work that actually fits your life."
        accentWord="fits"
        subtitle="We don't blast your CV around town. We listen, then we make the introductions worth your time."
      />

      <section className="py-20 md:py-28 bg-cream" data-testid="candidate-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">How it works for you</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              Four conversations between you and a job you'll love.
            </h2>
            <p className="mt-6 font-body text-ink-700 leading-relaxed">
              No portals, no auto-rejection emails, no being ghosted. Just one
              person who knows your search end-to-end.
            </p>
            <div className="mt-10 bg-mint rounded-2xl p-6 flex items-center gap-4">
              <SmileFigure className="w-20 h-24 flex-shrink-0" stroke="#2D3033" strokeWidth={4} />
              <p className="font-headings text-xl text-ink leading-tight">
                A human partner from first chat to first day.
              </p>
            </div>
          </div>
          <div className="lg:col-span-8">
            <ProcessDiagram steps={CANDIDATE_STEPS} accent="ink" />
          </div>
        </div>
      </section>

      {/* Live jobs */}
      <section className="py-20 md:py-28 bg-mint-soft" data-testid="jobs-listing">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">
                Jobs currently open · {jobs.length}
              </p>
              <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
                Live roles across Aotearoa.
              </h2>
            </div>
            <p className="font-body text-sm text-ink-700 max-w-sm">
              Many of our roles never hit a job board. If nothing below fits, tell
              us what you're looking for — we'll be in touch when it turns up.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {jobs.map((job, idx) => (
              <article
                key={idx}
                data-testid={`job-listing-${idx}`}
                className="bg-white rounded-2xl border border-ink-100 p-8 md:p-10 flex flex-col hover:shadow-md hover:-translate-y-1 transition-all"
              >
                <div className="flex items-center gap-2 text-sm font-body text-stone-dark mb-4">
                  <MapPin size={14} strokeWidth={1.5} />
                  <span>{job.location}</span>
                </div>
                <h3 className="font-headings text-2xl md:text-3xl text-ink leading-tight mb-4">
                  {job.title}
                </h3>
                <p className="font-body text-ink-700 leading-relaxed flex-1">
                  {job.description}
                </p>
                <div className="mt-8 pt-6 border-t border-ink-100 flex items-center justify-between gap-4">
                  <a
                    href={buildMailto(job)}
                    data-testid={`job-apply-${idx}`}
                    className="inline-flex items-center gap-2 bg-ink text-mint hover:bg-stone-dark px-6 py-3 rounded-full font-body font-medium text-sm transition-transform hover:-translate-y-0.5"
                  >
                    <Mail size={16} strokeWidth={1.75} />
                    Apply — email your CV
                  </a>
                  <span
                    className="text-xs text-stone-dark font-body hidden sm:inline"
                    title={APPLY_EMAIL}
                  >
                    to Bronwyn
                  </span>
                </div>
              </article>
            ))}
          </div>

          <p className="mt-10 font-body text-sm text-stone-dark text-center max-w-2xl mx-auto">
            Tapping <em>Apply</em> opens your email client with the role and
            Bronwyn's address pre-filled. Attach your CV and hit send — she reads
            every application personally.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-cream" data-testid="candidate-registration">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">
              Nothing above quite right?
            </p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              Register your interest — most roles never hit a job board.
            </h2>
            <p className="mt-6 font-body text-ink-700 max-w-2xl mx-auto leading-relaxed">
              Drop us your details and we'll be in touch when something genuinely fits. No spam, ever.
            </p>
          </div>
          <HubspotPlaceholder formType="candidate" />
        </div>
      </section>
    </div>
  );
}
